import _ from 'lodash';
import config from 'client/shared/config';

import {
	observable,
	action,
	reaction,
	computed,
	runInAction
} from 'mobx';

import BaseStore from './base-store';
import apiStore from './api-store';
import songsStore from './songs-store';
import Album from './album';

const { routes } = config;

function removeBadChars (value) {
	return value
		.replace(/['|"|,|`]/g, '')
		.trim()
		.replace(/\s/g, '_');
}

class AlbumsStore extends BaseStore {
	apiRoute = routes.albums;

	constructor () {
		super();

		this.disposeReaction = reaction(
			() => apiStore.data.albums,
			this.onFetchSuccess
		);

		this.songsStore = songsStore;
	}

	destroy () {
		this.disposeReaction();
	}

	/**
	 * Observables & Computeds
	 */

	@observable
	_albumModalOpen = false;

	@computed
	get albumModalOpen () {
		return this._albumModalOpen;
	}

	set albumModalOpen (albumModalOpen) {
		this._albumModalOpen = albumModalOpen;
	}



   @observable
   _modalEditAlbum = false;

   @computed
   get modalEditAlbum () {
	   return this._modalEditAlbum;
   }

   set modalEditAlbum (modalEditAlbum) {
	   this._modalEditAlbum = modalEditAlbum;
   }

	@observable
	_isSaving = true;

	@computed
	get isSaving () {
		return this._isSaving;
	}

	set isSaving (isSaving) {
		this._isSaving = isSaving;
	}

	@computed
	get albums () {
		return this._items;
	}

	set albums (albums) {
		this._items = albums;
	}

	/**
	 * Actions
	 */

	@action.bound
	async fetchAlbumsByArtist (artistID) {
		const route = routes.albumssyArtist.replace(/:artistID/, artistID);

		this.setIsLoading(true);

		try {

			await this.fetch({ route });
		} catch (error) {

			console.error('[ERROR SongsStore.fetchByArtist]', error);
		} finally {

			this.setIsLoading(false);
		}
	}

	@action.bound
	onFetchSuccess (albums = []) {
		if (_.isEmpty(albums)) {

			return;
		}

		this.albums = albums.map((album) => new Album(this, album));
		this.isLoading = false;
	}

	@action
	async saveAlbumArtwork ({ imageData, artist, user, album }) {
		const payload = this._getArtworkPayload({ user, artist, albumName: album.name });
		const opts = {
			method: 'put',
			route: routes.albumArtworkUpload.replace(/\:id/, album.id)
		};

		// apiStore.save flips method to POST when payload has no id, even
		// when the caller passes method: 'put'. Include _id so PUT survives.
		const data = await this.save({ _id: album.id, imageData, ...payload }, opts);

		album.update(data);
	}

	@action
	async saveAlbum ({ album, artist, user }) {
		const { id } = album;
		const isUpdate = _.isEmpty(id) !== true;

		const { artistID, artwork, linerNotes, name, songs } = album.serialized;

		// `artwork` is either a saved filename (existing image) or a base64
		// data URI (user just uploaded a new one). On create, the POST route
		// chains the S3 upload middleware so we send imageData inline. On
		// update, the PATCH route does NOT — it would just persist the base64
		// blob as a useless Mongo field and never write to S3. For updates we
		// strip imageData from the PATCH and call the artwork PUT endpoint
		// separately afterward.
		const isNewImage = _.isString(artwork) && artwork.startsWith('data:');

		this.isSaving = true;

		if (isUpdate) {
			const payload = {
				_id: id,
				artistID, linerNotes, name, songs,
			};

			const data = await this.save(payload, {
				route: routes.album.replace(/\:id/, `update/${id}`),
				method: 'patch',
			}).catch(({ message }) => console.error(message));

			album.update(data);

			if (isNewImage) {
				await this.saveAlbumArtwork({
					imageData: artwork,
					artist, user, album,
				});
			}
		} else {
			const payload = {
				fileName: `album-artwork-${new Date().getTime()}`,
				imageData: artwork,
				artistID, linerNotes, name, songs,
			};

			const data = await this.save(payload, {
				route: routes.createAlbum,
				method: 'post',
			}).catch(({ message }) => console.error(message));

			album.update(data);
			this._items.push(album);
		}

		this.isSaving = false;
	}

	/**
	 * Methods
	 */

	_getArtworkPayload ({ artist, user, albumName }) {
		const fileName = `album-artwork-${new Date().getTime()}`;
		const path = `album-artwork/${user.id}/${removeBadChars(artist.name)}/${removeBadChars(albumName)}`;

		return { fileName, path };
	}

	newAlbum () {
		const album = new Album(this);
		this.albums.unshift(album);

		return album;
	}

	getAlbumByID (id) {
		return this.getItemByID(id);
	}

	getAlbumsByArtist (id) {
		return this.albums.filter(({ artistID }) => artistID === id);
	}

	getAlbumArtwork (id) {
		const album = this.getAlbumByID(id);
		return _.isEmpty(album) ? null : album.artworkPath;
	}

	getAlbumsForSong ({ id }) {
		return this.albums.filter(({ songIDs = [] }) => songIDs.indexOf(id) > -1);
	}

	removeUnsavedAlbums () {
		this.albums = this.albums.filter(({ id }) => _.isString(id));
	}
}

const albumsStore = new AlbumsStore();
export default albumsStore;
